import { z } from 'zod';

import { httpClientInterface } from 'app-components/utils/core';
import { getServiceUrl } from 'app-components/utils/core/interfaces/httpClientInterface';

import type {
  AddBillScoutRecordedTransactionsPayload,
  BillScoutRecordedTransaction,
} from './billScoutRecordedTransactionsTypes';

const RecordedTransactionResourceSchema = z.object({
  id: z.string(),
  transactionId: z.string(),
  recordedScheduleId: z.string(),
  isDeleted: z.boolean().optional(),
});

const RecordedTransactionsResponseSchema = z.object({
  resources: z.array(RecordedTransactionResourceSchema),
});

type RecordedTransactionResource = z.infer<typeof RecordedTransactionResourceSchema>;

const toBillScoutRecordedTransaction = (resource: RecordedTransactionResource): BillScoutRecordedTransaction => ({
  id: resource.id,
  transactionId: resource.transactionId,
  recordedScheduleId: resource.recordedScheduleId,
});

const parseRecordedTransactionsResponse = (data: unknown): BillScoutRecordedTransaction[] =>
  RecordedTransactionsResponseSchema.parse(data)
    .resources.filter((resource) => !resource.isDeleted)
    .map(toBillScoutRecordedTransaction);

const getRecordedTransactionsUrl = (): string => `${getServiceUrl()}/bill-scout/recorded-transactions`;

export const billScoutRecordedTransactionsApi = {
  async fetch(stModelId: string): Promise<BillScoutRecordedTransaction[]> {
    const response = await httpClientInterface.send('GET', getRecordedTransactionsUrl(), undefined, undefined, {
      stModelId,
    });
    return parseRecordedTransactionsResponse(response.data);
  },

  async add({
    stModelId,
    transactionIds,
  }: AddBillScoutRecordedTransactionsPayload): Promise<BillScoutRecordedTransaction[]> {
    const response = await httpClientInterface.send('POST', getRecordedTransactionsUrl(), {
      stModelId,
      transactionIds: [...new Set(transactionIds)],
    });
    return parseRecordedTransactionsResponse(response.data);
  },

  async delete(recordedTransactionId: string): Promise<void> {
    const requestUrl = `${getRecordedTransactionsUrl()}/${encodeURIComponent(recordedTransactionId)}`;
    await httpClientInterface.send('DELETE', requestUrl);
  },
} as const;
